import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Step,
  Stepper,
  useMediaQuery,
} from "@mui/material";
import ChartIcon from "@mui/icons-material/BarChart";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PropTypes from "prop-types";
import { useFormik } from "formik";

import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import Tooltip from "Components/Common/Tooltip";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Backdrop from "Components/Common/Backdrop";
import Modal from "Components/Common/Modal";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import StepLabelField from "common/ApplicationApprovalTimelineChart/StepLabelField";
import {
  DCS_CONSUMER_BILLING_INCHARGE,
  DCS_CONSUMER_BILLING_OPERATOR,
  DCS_FINANCE,
  otherUtilitiesAdmin2Roles,
  otherUtilitiesBillingAdmin3Roles,
  otherUtilitiesBillingAdmin4Roles,
  POWER_CONSUMER_BILLING_INCHARGE,
  POWER_CONSUMER_BILLING_OPERATOR,
  POWER_BANKING_FINANCE,
  POWER_FINANCE,
  SUPER_ADMIN,
} from "common/constant";
import {
  billingSubscriptionTypes,
  calculationStatusEnum,
  getOptionLabel,
  getOptionValue,
} from "common/enums";
import DispatchBillModel from "./DispatchBillModel";
import BillReviewModel from "./BillReviewModel";

import {
  useLazyBillingCalculationQuery,
  useLazyGetBillingCycleChartQuery,
  usePutManualDispatchBilling,
} from "apis/Billing";
import {
  useGetAllSourceOfPowerQuery,
  useGetBillingCycleOptionsQuery,
  useLazyGetAllSourceOfPowerQuery,
} from "apis/BillingCycle";
import { getImageUrl, isAuthorizedUser } from "utils";

const ConsumerBillDetails = ({ t }) => {
  const { roles } = useSelector((state) => state.Login);

  const [searchParams, setSearchParams] = useSearchParams();
  const billingCycleId = searchParams.get("billingCycleId");
  const [documentToPreview, setDocumentToPreview] = useState("");
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState("");
  const [billCycleIdForChart, setBillCycleIdForChart] = useState("");
  const [billCycleIdForDownloadBtn, setBillCycleIdForDownloadBtn] =
    useState("");
  const [manualDispatchIds, setManualDispatchIds] = useState([]);
  const resetPageRef = useRef();
  const isOtherUtility = useMemo(
    () =>
      getOptionValue("billingSubscriptionTypes", "Electricity") !==
      subscriptionType,
    [subscriptionType]
  );
  const [
    getBillCalculationApprovalData,
    {
      data: { data: billCalculationApprovalTrackingData = [] } = {},
      isLoading,
      isFetching,
    },
  ] = useLazyGetBillingCycleChartQuery();

  const [updateManualDispatch, { isLoading: manualDispatchLoading }] =
    usePutManualDispatchBilling();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");
  const isMobileDevice = useMediaQuery("(max-width: 768px)");

  const {
    data: { data: billingCycleOptions } = {},
    isLoading: isBillingCycleOptionsLoading,
  } = useGetBillingCycleOptionsQuery();

  // const {
  //   data: { data: sourceOfPowerOptions } = {},
  //   isLoading: isSourceOfPowerOptionsLoading,
  // } = useGetAllSourceOfPowerQuery(undefined, {
  //   skip: !isOtherUtility,
  // });

  const [
    getAllSourceOfPower,
    {
      data: { data: sourceOfPowerOptions } = {},
      isLoading: isSourceOfPowerOptionsLoading,
    },
  ] = useLazyGetAllSourceOfPowerQuery();

  const [
    getBillingCalculations,
    {
      data: { data: billingCalculations } = {},
      isLoading: isBillingCalculationLoading,
      isFetching: isBillingCalculationFetching,
    },
  ] = useLazyBillingCalculationQuery();

  const [isDispatchBtnClicked, setIsDispatchBtnClicked] = useState(false);

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter((option) => option.subscriptionType === subscriptionType)
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions, subscriptionType]
  );

  const updatedSourceOfPowerOptions = useMemo(
    () =>
      sourceOfPowerOptions?.length
        ? sourceOfPowerOptions.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [billingCycleOptions, subscriptionType]
  );

  const updatedBillingSubscriptionOptions = useMemo(
    () =>
      billingSubscriptionTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [billingSubscriptionTypes]
  );

  const isManualDispatchIconVisible = useMemo(
    () =>
      isAuthorizedUser(roles, [
        SUPER_ADMIN,
        POWER_FINANCE,
        POWER_BANKING_FINANCE,
        POWER_CONSUMER_BILLING_INCHARGE,
      ]) &&
      subscriptionType ===
        getOptionValue("billingSubscriptionTypes", "Electricity"),
    [roles, subscriptionType]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      billCycleId:
        updatedBillingCycleOptions.find(
          ({ status }) => status !== getOptionValue("billingStatus", "Closed")
        )?.value ??
        updatedBillingCycleOptions.at(-1)?.value ??
        "",
      consumerNumber: "",
      meterNumber: "",
      calculationStatus: "",
      consumerName: "",
      buildingName: "",
      sourceOfPowerId: "",
    },
    onSubmit: async ({
      billCycleId,
      consumerNumber,
      meterNumber,
      calculationStatus,
      consumerName,
      buildingName,
      sourceOfPowerId,
    }) => {
      try {
        await getBillingCalculations(billCycleId, {
          consumerNumber,
          meterNumber,
          calculationStatus,
          consumerName,
          buildingName,
          sourceOfPowerId,
        });
        handleResetPage();
      } catch (error) {}
    },
  });

  const {
    handleSubmit,
    setFieldValue,
    values: { billCycleId },
  } = formik;

  const columns = [
    { title: "Bill Cycle", field: "billCycle" },
    { title: "Consumer Number", field: "consumerNumber" },
    { title: "Meter Number", field: "meterNumber" },
    { title: "Consumer Name", field: "consumerName" },
    { title: "Tariff Name", field: "tariffName" },
    { title: "Net Payable", field: "netPayable" },

    ...(!isOtherUtility
      ? [
          { title: "Source of Power", field: "sourceOfPower" },
          { title: "Building Name", field: "buildingName" },
        ]
      : []),
    {
      title: "Status",
      renderCell: ({ status }) =>
        getOptionLabel("billCalculationStatus", status),
    },
    {
      title: "Actions",
      renderCell: ({ url, approvedBy, revokedBy, id, status }) => (
        <Box minWidth="100px">
          <IconButton
            onClick={() =>
              setDocumentToPreview({
                url: getImageUrl(url),
                documentTypeName: "Bill",
                documentType: "pdf",
                documentName: "bill",
              })
            }
          >
            <Tooltip title={t("View Bill Receipt")}>
              <ViewIcon
                fontSize="small"
                color="primary"
                className="cursor-pointer"
              />
            </Tooltip>
          </IconButton>
          {!!(approvedBy || revokedBy) && (
            <IconButton
              onClick={async () => {
                try {
                  await getBillCalculationApprovalData(id[0], {
                    subscriptionType,
                  });
                  setBillCycleIdForChart(id);
                } catch (error) {
                  console.error(error);
                }
              }}
            >
              <Tooltip title={t("Approval History Timeline Chart")}>
                <ChartIcon
                  fontSize="medium"
                  color="primary"
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>
          )}
          {isManualDispatchIconVisible &&
            ["Default", "Approved", "Signed"].includes(
              getOptionLabel("billCalculationStatus", status)
            ) && (
              <IconButton onClick={() => setManualDispatchIds(id)}>
                <Tooltip title={t("Manual Dispatch")}>
                  <FactCheckIcon
                    fontSize="medium"
                    color="primary"
                    className="cursor-pointer"
                  />
                </Tooltip>
              </IconButton>
            )}
        </Box>
      ),
    },
  ];

  const {
    isActionButtonVisible,
    isBillingInchargeAdmin,
    isOtherUtilityFinanceAdmin,
  } = useMemo(
    () => ({
      isActionButtonVisible: isAuthorizedUser(roles, [
        POWER_CONSUMER_BILLING_OPERATOR,
        POWER_CONSUMER_BILLING_INCHARGE,
        DCS_CONSUMER_BILLING_INCHARGE,
        DCS_CONSUMER_BILLING_OPERATOR,
        DCS_FINANCE,
        ...otherUtilitiesAdmin2Roles,
        ...otherUtilitiesBillingAdmin3Roles,
        ...otherUtilitiesBillingAdmin4Roles,
      ]),
      isBillingInchargeAdmin: isAuthorizedUser(roles, [
        POWER_CONSUMER_BILLING_INCHARGE,
        DCS_CONSUMER_BILLING_INCHARGE,
        ...otherUtilitiesBillingAdmin3Roles,
      ]),
      isOtherUtilityFinanceAdmin: isAuthorizedUser(roles, [
        DCS_FINANCE,
        ...otherUtilitiesAdmin2Roles,
      ]),
    }),
    [roles]
  );

  const submitHandlerForManualDispatch = async () => {
    try {
      await updateManualDispatch(
        manualDispatchIds,
        "Manual Dispatched Successfull"
      );

      setManualDispatchIds([]);
    } catch (err) {
      console.error(err);
    }
  };

  const clearBtnHandler = async () => {
    try {
      setFieldValue("consumerNumber", "");
      setFieldValue("meterNumber", "");
      setFieldValue("consumerName", "");
      setFieldValue("buildingName", "");
      setFieldValue("sourceOfPowerId", "");
      await getBillingCalculations(billCycleId);
      handleResetPage();
    } catch (error) {}
  };

  useEffect(() => {
    (async () => {
      try {
        if (updatedBillingCycleOptions.length) {
          await getBillingCalculations(
            updatedBillingCycleOptions.find(
              ({ status }) =>
                status !== getOptionValue("billingStatus", "Closed")
            )?.value ??
              updatedBillingCycleOptions.at(-1)?.value ??
              ""
          );
        }
      } catch (error) {}
    })();
  }, [updatedBillingCycleOptions]);

  useEffect(() => {
    if (updatedBillingSubscriptionOptions.length) {
      setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
    }
  }, [updatedBillingSubscriptionOptions]);

  useEffect(() => {
    (async () => {
      if (!isOtherUtility) {
        try {
          await getAllSourceOfPower();
        } catch (err) {}
      }
    })();
  }, [isOtherUtility]);

  return (
    <div>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}
      {!!manualDispatchIds.length && (
        <ConfirmationDialog
          message="Are you sure you want to dispatch it manually?"
          onCancel={() => setManualDispatchIds([])}
          onConfirm={submitHandlerForManualDispatch}
          isOpen
          title="Warning"
          variant="warning"
          isLoading={manualDispatchLoading}
        />
      )}
      <Backdrop
        open={
          isBillingCycleOptionsLoading ||
          isBillingCalculationLoading ||
          isBillingCalculationFetching ||
          isLoading ||
          isFetching ||
          isSourceOfPowerOptionsLoading
        }
      />

      {!!billCycleIdForChart && (
        <Modal
          isOpen
          title="Approval Chart"
          maxWidth="lg"
          handleClose={() => setBillCycleIdForChart("")}
        >
          <Box sx={{ width: "100%" }}>
            <Stepper
              activeStep={billCalculationApprovalTrackingData.length}
              alternativeLabel={!isMobileDevice}
              className="mt-3"
              orientation={isMobileDevice ? "vertical" : "horizontal"}
            >
              {billCalculationApprovalTrackingData.map((step, index) => (
                <Step key={index}>
                  <StepLabelField step={step} />
                </Step>
              ))}
            </Stepper>
          </Box>
        </Modal>
      )}

      {isDispatchBtnClicked && (
        <DispatchBillModel
          handleClose={() => setIsDispatchBtnClicked(false)}
          subscriptionType={subscriptionType}
          billCycleId={billCycleId}
        />
      )}

      {(!!billingCycleId || !!billCycleIdForDownloadBtn) && (
        <BillReviewModel
          handleClose={() => {
            searchParams.delete("billingCycleId");
            setSearchParams(searchParams);
            setBillCycleIdForDownloadBtn("");
          }}
          subscriptionType={subscriptionType}
          billCycleIdForDownloadBtn={billCycleIdForDownloadBtn}
        />
      )}

      <BreadCrumb title={t("Consumer Bills")} pageTitle={t("Billing")} />
      <form onSubmit={handleSubmit}>
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Subscription Type") + "*"}
              onChange={(_, { value }) => setSubscriptionType(value)}
              value={
                updatedBillingSubscriptionOptions.find(
                  ({ value }) => value === subscriptionType
                ) ?? ""
              }
              options={updatedBillingSubscriptionOptions}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Select bill cycle")}
              name="billCycleId"
              formik={formik}
              options={updatedBillingCycleOptions || []}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Consumer Number")}
              name="consumerNumber"
              formik={formik}
              placeholder={t("Enter consumer number")}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Meter Number")}
              name="meterNumber"
              formik={formik}
              placeholder={t("Enter meter number")}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Status")}
              name="calculationStatus"
              formik={formik}
              options={calculationStatusEnum || []}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Consumer Name")}
              name="consumerName"
              formik={formik}
              placeholder={t("Enter consumer name")}
            />
          </Grid>
          {!isOtherUtility && (
            <>
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <InputFieldText
                  label={t("Building Name")}
                  name="buildingName"
                  formik={formik}
                  placeholder={t("Enter building name")}
                />
              </Grid>

              <Grid item lg={3} md={4} sm={6} xs={12}>
                <SelectField
                  label={t("Select Source of power")}
                  name="sourceOfPowerId"
                  formik={formik}
                  options={updatedSourceOfPowerOptions || []}
                />
              </Grid>
            </>
          )}

          <Grid item>
            <Button
              variant="outlined"
              className="mb-2 me-2"
              type="submit"
              disabled={!billCycleId}
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={clearBtnHandler}
              disabled={!billCycleId}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
          </Grid>
          {!!billingCalculations?.length && isActionButtonVisible && (
            <Grid item>
              <>
                {isBillingInchargeAdmin && (
                  <Button
                    onClick={() => setIsDispatchBtnClicked(true)}
                    variant="contained"
                    sx={{ fontSize: "13px" }}
                    className="mb-2 me-2"
                  >
                    {t("Dispatch")}
                  </Button>
                )}
                {(!isOtherUtilityFinanceAdmin ||
                  (isOtherUtilityFinanceAdmin && isOtherUtility)) && (
                  <Button
                    onClick={() => {
                      searchParams.set("billingCycleId", billCycleId);
                      setSearchParams(searchParams);
                    }}
                    sx={{ fontSize: "13px" }}
                    className="mb-2 me-2"
                    variant="contained"
                  >
                    {t(
                      isOtherUtilityFinanceAdmin
                        ? "Generate E-Invoice"
                        : "Review Bills"
                    )}
                  </Button>
                )}
                {isOtherUtility && (
                  <Button
                    variant="contained"
                    className="mb-2 me-2"
                    onClick={() => {
                      setBillCycleIdForDownloadBtn(billCycleId);
                    }}
                  >
                    {t("Download")}
                  </Button>
                )}
              </>
            </Grid>
          )}
        </Grid>
      </form>
      <BaseTable
        data={billingCalculations}
        columns={columns}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        pageSize={10}
        displaySrNo
        maxHeight={
          sm
            ? "calc(100vh - 350px)"
            : md
            ? "calc(100vh - 300px)"
            : "calc(100vh - 250px)"
        }
      />
    </div>
  );
};

ConsumerBillDetails.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(ConsumerBillDetails);
